import { useSQLiteContext } from 'expo-sqlite';
import { useEffect, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import {
  getServiceHistoryForSystem,
  type ServiceHistoryEntry,
} from '@/lib/tasks';
import { resolveIcon } from '@/lib/rule-icon';

export function ServiceHistoryList({ systemId }: { systemId: string }) {
  const db = useSQLiteContext();
  const [history, setHistory] = useState<ServiceHistoryEntry[]>([]);

  useEffect(() => {
    (async () => {
      setHistory(await getServiceHistoryForSystem(db, systemId));
    })();
  }, [db, systemId]);

  const totalCents = history.reduce((sum, h) => sum + (h.cost_cents ?? 0), 0);

  return (
    <View style={styles.wrap}>
      <View style={styles.head}>
        <Text style={styles.title}>Service history · {history.length}</Text>
        {totalCents > 0 ? (
          <Text style={styles.total}>${(totalCents / 100).toLocaleString()} on file</Text>
        ) : null}
      </View>

      {history.length === 0 ? (
        <Text style={styles.empty}>
          Nothing logged yet. When you mark tasks done, anything you add (cost,
          contractor, notes) shows up here — and on the future resale binder.
        </Text>
      ) : (
        <View style={{ gap: 8, marginTop: 10 }}>
          {history.map((h) => {
            const Icon = resolveIcon(h.icon);
            const date = new Date(h.completed_at).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            });
            const meta = [
              date,
              h.contact_name ?? h.vendor,
              h.cost_cents ? `$${(h.cost_cents / 100).toLocaleString()}` : null,
            ].filter(Boolean);
            return (
              <View key={h.completion_id} style={styles.row}>
                <View style={styles.icoBox}>
                  <Icon size={18} color={Palette.forest} strokeWidth={1.7} />
                </View>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.taskTitle} numberOfLines={1}>{h.task_title}</Text>
                  <Text style={styles.meta} numberOfLines={1}>{meta.join(' · ')}</Text>
                  {h.notes ? (
                    <Text style={styles.notes} numberOfLines={2}>{h.notes}</Text>
                  ) : null}
                </View>
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.xl,
    padding: Spacing.four,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.inkFaint,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  total: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.honeyDeep,
  },
  empty: {
    marginTop: 10,
    fontFamily: Fonts.sansMedium,
    fontSize: 13.5,
    color: Palette.inkSoft,
    lineHeight: 19,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 11,
    backgroundColor: Palette.sand,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: 14,
    padding: 11,
  },
  icoBox: {
    width: 36,
    height: 36,
    borderRadius: 11,
    backgroundColor: '#EAF0EA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  taskTitle: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  meta: { fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft, marginTop: 2 },
  notes: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    marginTop: 4,
    lineHeight: 17,
  },
});
